import {ReactElement, CSSProperties, useEffect, useMemo, useState } from "react";
import Icon from "../../Icon/Icon";

import d2Icon from "../../../../assets/images/common/svg/dice/d2.svg";
import d4Icon from "../../../../assets/images/common/svg/dice/d4.svg";
import d6Icon from "../../../../assets/images/common/svg/dice/d6.svg";
import d8Icon from "../../../../assets/images/common/svg/dice/d8.svg";
import d10Icon from "../../../../assets/images/common/svg/dice/d10.svg";
import d12Icon from "../../../../assets/images/common/svg/dice/d12.svg";
import d20Icon from "../../../../assets/images/common/svg/dice/d20.svg";

import "./DiceRollDisplay_style.css";

type Props = Readonly<{
    diceSides: number, 
    size?: number, 
    externalRollTriggerTimestamp?: number | null,
    hasRollTimeStagger?: boolean
}>;

const DEFAULT_SIZE = 64;

export default function DiceRollDisplay({
    diceSides, 
    size = DEFAULT_SIZE, 
    externalRollTriggerTimestamp = 0,
    hasRollTimeStagger = true,
}: Props): ReactElement {
    const iconImageSrc = useMemo(() => {
        switch(diceSides) {
            case 4:
                return d4Icon;
            case 6:
                return d6Icon;
            case 8:
                return d8Icon;
            case 10:
            case 100:
                return d10Icon;
            case 12:
                return d12Icon;
            case 20:
                return d20Icon;
            case 2:
            default:
                return d2Icon;
        }
    }, [diceSides]);
    
    const [rollStyle, setRollStyle] = useState<CSSProperties>({});
    const [rollValue, setRollValue] = useState<number>(1);
    const [rollTimeoutID, setRollTimeoutID] = useState<any>(null);
    const [lastRollTimestamp, setLastRollTimestamp] = useState<number | null>(null);

    const startSpin = (spinDurationSec: number = 1) => {
        if(rollTimeoutID != null) {
            clearTimeout(rollTimeoutID);
        }
        setRollStyle({});

        const spinTime = hasRollTimeStagger ? spinDurationSec * (1 + (Math.random() * .75)) : spinDurationSec;

        setTimeout(() => {
            const isEven = Math.floor((Math.random() * 2)) % 2 === 0;
            setRollStyle({animationName: isEven ? "dice_spin" : "dice_spin_reverse", animationIterationCount: 1, animationDuration: `${spinTime}s`, animationTimingFunction: "ease-out"});
            setRollValue(Math.ceil(Math.random() * diceSides));

            setRollTimeoutID(setTimeout(() => {
                setRollStyle({});
            }, spinTime * 1000));
        }, 100);
    }

    const fontSize = useMemo(() => `${size / DEFAULT_SIZE}em`, [size]);

    /* starSpin intentionally omitted from dependencies */
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if(externalRollTriggerTimestamp != null) {
            if(externalRollTriggerTimestamp > (lastRollTimestamp ?? 0)) { 
                startSpin();
                setLastRollTimestamp(externalRollTriggerTimestamp);
            }
        }
    }, [externalRollTriggerTimestamp]);
    /* eslint-enable react-hooks/exhaustive-deps */

    return <div className="dice-roll-component" style={{width: size, height: size, ...rollStyle}}>
        <div className="dice-display-container" onClick={() => {startSpin()}}>
            <Icon className="dice-icon" src={iconImageSrc} size={size} />
            <div className={`dice-value${diceSides === 4 ? " d4" : diceSides === 20 ? " d20" : ""}`} style={{fontSize}}>
                {rollValue}
            </div>
        </div>
    </div>;
}